import ApplicationController from '../../../../../javascript/controllers/application_controller'

/**
 * Identifier Controller
 *
 * Manages visibility of identifier input fields based on selected type.
 * Tracks identifier type changes and reports validation errors.
 *
 * This controller manages the visibility of different identifier input fields
 * based on the selected identifier type (ISBN, DOI, PMID, or standard).
 * It shows the appropriate input field and hides all others when the user
 * selects a different identifier type from the dropdown.
 *
 * Breadcrumbs:
 *   - Form/identifier_type_changed: When user changes identifier type
 *
 * Error Handling:
 *   - Reports invalid identifier type selections to AppSignal
 *
 * @extends ApplicationController
 *
 * @example
 * <div data-controller="views--publication--import--index-component--identifier"
 *      data-views--publication--import--index-component--identifier-default-value="isbn">
 *   <select data-views--publication--import--index-component--identifier-target="select">...</select>
 *   <div data-views--publication--import--index-component--identifier-target="isbn">...</div>
 *   <div data-views--publication--import--index-component--identifier-target="doi">...</div>
 *   <div data-views--publication--import--index-component--identifier-target="pmid">...</div>
 *   <div data-views--publication--import--index-component--identifier-target="standard">...</div>
 * </div>
 */
export default class extends ApplicationController {
  /**
   * Define Stimulus targets
   * @property {Element} selectTarget - The select element for choosing identifier type
   * @property {Element} isbnTarget - The container for ISBN input
   * @property {Element} doiTarget - The container for DOI input
   * @property {Element} pmidTarget - The container for PMID input
   * @property {Element} standardTarget - The container for standard input
   */
  static targets = ['select', 'isbn', 'doi', 'pmid', 'arxivId', 'standard']

  /**
   * Define Stimulus values
   * @property {String} defaultValue - The default identifier type
   */
  static values = {
    default: String,
  }

  /**
   * Define Stimulus CSS classes
   * @property {String} hiddenClass - The CSS class to hide elements
   */
  static classes = ['hidden']

  /**
   * Define allowed identifier types
   */
  static allowedValues = ['isbn', 'doi', 'pmid', 'arxiv_id', 'standard']

  /**
   * Initialize the controller when it connects to the DOM
   */
  connect() {
    // Set default hidden class if not provided
    if (!this.hasHiddenClass) {
      this.hiddenClass = 'hidden'
    }

    this.initializeSelectValue()
    this.updateElementsVisibility()
  }

  /**
   * Set the select element's value to the default value
   */
  initializeSelectValue() {
    if (this.hasSelectTarget && this.defaultValue) {
      this.selectTarget.value = this.defaultValue
    }
  }

  /**
   * Update the visibility of identifier input fields based on the selected value
   * @param {Event} event - The change event from the select element (optional)
   */
  updateElementsVisibility(event) {
    const selectedValue = event ? event.target.value : this.defaultValue

    // Validate the selected value
    if (!this.constructor.allowedValues.includes(selectedValue)) {
      this.reportError(
        new Error(`Invalid identifier type: ${selectedValue}`),
        'Invalid identifier type selected',
        {
          selectedValue,
          allowedValues: this.constructor.allowedValues,
        },
      )
      return
    }

    // Add breadcrumb for successful changes
    if (event) {
      // Only track user-initiated changes
      this.addBreadcrumb('Form', 'identifier_type_changed', {
        type: selectedValue,
      })
    }

    // Hide all targets
    this.hideAllTargets()

    // Show the selected target
    this.showTarget(selectedValue)
  }

  /**
   * Convert a snake_case string to camelCase
   * @param {String} str - The string to convert
   * @returns {String} The camelCase string
   */
  snakeToCamel(str) {
    return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
  }

  /**
   * Hide all identifier input fields
   */
  hideAllTargets() {
    this.constructor.allowedValues.forEach((type) => {
      const camelType = this.snakeToCamel(type)
      const targetName = `${camelType}Target`
      const hasTarget = `has${camelType.charAt(0).toUpperCase() + camelType.slice(1)}Target`

      if (this[hasTarget]) {
        this[targetName].classList.add(this.hiddenClass)
      }
    })
  }

  /**
   * Show the specified identifier input field
   * @param {String} type - The identifier type to show
   */
  showTarget(type) {
    const camelType = this.snakeToCamel(type)
    const targetName = `${camelType}Target`
    const hasTarget = `has${camelType.charAt(0).toUpperCase() + camelType.slice(1)}Target`

    if (this[hasTarget]) {
      this[targetName].classList.remove(this.hiddenClass)
    }
  }
}
